import { useMemo, useState } from 'react'
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LuSparkles,
  LuInfo,
  LuScanEye,
  LuShieldCheck,
  LuMessageSquareText
} from 'react-icons/lu'
import { H6 } from '@/components/ui/Headings'
import { Tooltip } from '@/components/ui/Tooltip'
import { useBrandContext } from '@/context/brand-context'
import { useDesignReview, useTriggerDesignReview } from '@/api/design-review'
import ReviewScoreTable from './review-score-table'
import ProPluginsPanel from './pro-plugins-section'

import type { Design } from '@/api/designs'

type MetadataTab = 'scores' | 'information' | 'integrations'

const tabs: { key: MetadataTab; label: string; badge?: string }[] = [
  { key: 'scores', label: 'Scores' },
  { key: 'information', label: 'Information' },
  { key: 'integrations', label: 'Integrations', badge: 'PRO' }
]

interface DesignMetadataPanelProps {
  design: Design
}

function toSentenceCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

function useBrandPayload() {
  const { brandAnalysis } = useBrandContext()
  return useMemo(() => {
    if (!brandAnalysis) return null
    return {
      brand: {
        name: brandAnalysis.brand.name,
        colors: brandAnalysis.brand.colors,
        visualStyle: brandAnalysis.brand.visualStyle,
        designDirective: brandAnalysis.brand.designDirective,
        keywords: brandAnalysis.brand.keywords,
        summary: brandAnalysis.brand.summary
      }
    } as Record<string, unknown>
  }, [brandAnalysis])
}

function DescriptionToggle({ description }: { description: string }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Box mt="2px">
      <Flex
        align="center"
        gap="5px"
        cursor="pointer"
        role="group"
        onClick={() => setIsOpen((v) => !v)}
        py="4px"
      >
        <Box
          color="whiteAlpha.400"
          display="flex"
          transition="color 0.2s ease"
          _groupHover={{ color: 'whiteAlpha.700' }}
        >
          <LuMessageSquareText size={12} />
        </Box>
        <Text
          textStyle="caption"
          color="whiteAlpha.400"
          transition="color 0.2s ease"
          _groupHover={{ color: 'whiteAlpha.700' }}
        >
          {isOpen ? 'Hide prompt' : 'Show prompt'}
        </Text>
      </Flex>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box
              px="10px"
              py="9px"
              borderRadius="10px"
              bg="rgba(255,255,255,0.04)"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
            >
              <Text textStyle="caption" color="whiteAlpha.700">
                {description}
              </Text>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default function DesignMetadataPanel({
  design
}: DesignMetadataPanelProps) {
  const [activeTab, setActiveTab] = useState<MetadataTab>('scores')

  const brandPayload = useBrandPayload()

  const {
    data: review,
    isError: noReviewExists
  } = useDesignReview(design.id)

  const isCompleted = review?.status === 'COMPLETED'
  const isFailed = review?.status === 'FAILED'
  const isPending = review?.status === 'PENDING'

  const triggerMutation = useTriggerDesignReview()

  const handleTriggerReview = () => {
    triggerMutation.mutate({
      designId: design.id,
      brandAnalysis: brandPayload
    })
  }

  const description = design.originalPrompt || design.augmentedPrompt
  const colorSwatches = design.paletteColors?.slice(0, 8) ?? []
  const visualStyle = design.visualStyle?.trim() || null
  const wildness = design.creativityLevel
    ? toSentenceCase(design.creativityLevel)
    : null
  const sellingScore = design.sellingScore != null ? design.sellingScore : null

  const aspectRatio = design.aspectRatio?.trim() || null

  const showReviewButton = noReviewExists && !isPending && !isCompleted && !isFailed

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <H6 color="whiteAlpha.900" mb="1px">
        {design.name}
      </H6>

      <Flex
        borderBottom="1px solid"
        borderColor="rgba(255,255,255,0.08)"
        gap="0"
        mt="12px"
        flexShrink={0}
      >
        {tabs.map((tab) => (
          <Box
            key={tab.key}
            px="10px"
            py="8px"
            cursor="pointer"
            borderBottom="2px solid"
            borderColor={activeTab === tab.key ? 'accent.500' : 'transparent'}
            transition="all 0.2s ease"
            onClick={() => setActiveTab(tab.key)}
            _hover={{
              borderColor: activeTab === tab.key ? 'accent.500' : 'rgba(255,255,255,0.16)'
            }}
          >
            <Flex align="center" gap="5px">
              <Text
                textStyle="bodySuperSmall"
                fontWeight={500}
                color={activeTab === tab.key ? 'whiteAlpha.900' : 'whiteAlpha.400'}
                transition="color 0.2s ease"
              >
                {tab.label}
              </Text>
              {tab.badge && (
                <Text
                  textStyle="label"
                  fontWeight={600}
                  px="5px"
                  py="1px"
                  borderRadius="9999px"
                  bg="rgba(255,255,255,0.06)"
                  color="whiteAlpha.700"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.12)"
                  textTransform="uppercase"
                  css={{ fontSize: '9px' }}
                >
                  {tab.badge}
                </Text>
              )}
            </Flex>
          </Box>
        ))}
      </Flex>

      <Box
        flex={1}
        overflowY="auto"
        mt="12px"
        css={{
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.14)',
            borderRadius: '4px'
          }
        }}
      >
      {activeTab === 'scores' && (
        <Flex flexDirection="column" gap="12px">
          {sellingScore != null && (
            <Box
              position="relative"
              overflow="hidden"
              px="12px"
              py="10px"
              borderRadius="12px"
              bg="rgba(0, 112, 243, 0.06)"
              border="1px solid"
              borderColor="rgba(0, 112, 243, 0.14)"
              css={{
                animation: 'borderGlow 3s ease-in-out infinite'
              }}
            >
              <Box
                position="absolute"
                inset={0}
                borderRadius="12px"
                className="animate-shimmer"
                pointerEvents="none"
              />

              <Flex align="center" justify="space-between" position="relative">
                <Flex align="center" gap="6px">
                  <Box color="accent.400" display="flex">
                    <LuSparkles size={13} />
                  </Box>
                  <Text textStyle="label" color="accent.200" letterSpacing="0.08em">
                    Selling Score
                  </Text>
                </Flex>

                <Flex align="center" gap="6px">
                  <Text
                    textStyle="subtitle"
                    fontWeight="600"
                    letterSpacing="-0.02em"
                    bgGradient="to-r"
                    gradientFrom="white"
                    gradientTo="accent.200"
                    bgClip="text"
                  >
                    {sellingScore}%
                  </Text>
                  <Tooltip
                    showArrow
                    content="Our system analyzed current market trends and competition to estimate how likely this design is to sell."
                    contentProps={{
                      maxW: '220px',
                      bg: 'bg.tertiary',
                      color: 'text.primary',
                      px: '10px',
                      py: '8px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'border.DEFAULT',
                      fontSize: '12px'
                    }}
                  >
                    <Box
                      color="accent.300"
                      display="flex"
                      cursor="pointer"
                      opacity={0.7}
                      transition="opacity 0.2s ease"
                      _hover={{ opacity: 1 }}
                    >
                      <LuInfo size={13} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Flex>
            </Box>
          )}

          {showReviewButton && (
            <Button
              w="100%"
              h="36px"
              px="20px"
              textStyle="bodySmall"
              fontWeight={500}
              borderRadius="9999px"
              bg="rgba(255, 255, 255, 0.06)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
              color="whiteAlpha.800"
              transition="all 0.3s ease"
              _hover={{
                bg: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.16)'
              }}
              loading={triggerMutation.isPending}
              onClick={handleTriggerReview}
            >
              <Flex align="center" gap="6px">
                <LuScanEye size={14} />
                Review with AI
              </Flex>
            </Button>
          )}

          {isPending && (
            <Box>
              <Flex align="center" gap="6px" mb="8px">
                <Box
                  color="accent.400"
                  display="flex"
                  css={{ animation: 'spin 2s linear infinite' }}
                >
                  <LuScanEye size={13} />
                </Box>
                <Text textStyle="label" color="accent.200" letterSpacing="0.08em">
                  Reviewing…
                </Text>
              </Flex>
              <Flex flexDirection="column" gap="6px">
                {[
                  'IP Infringement',
                  'Brand Adherence',
                  'Inappropriate Content',
                  'Print Quality',
                  'Color Harmony'
                ].map((label, i) => (
                  <Box
                    key={label}
                    px="10px"
                    py="8px"
                    borderRadius="10px"
                    bg="rgba(255,255,255,0.03)"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.06)"
                    overflow="hidden"
                    position="relative"
                    css={{
                      animation: `reviewRowFadeIn 0.4s ease ${i * 0.08}s both`
                    }}
                  >
                    <Box
                      position="absolute"
                      inset={0}
                      className="animate-shimmer"
                      pointerEvents="none"
                    />
                    <Flex
                      align="center"
                      justify="space-between"
                      position="relative"
                    >
                      <Text textStyle="bodySuperSmall" color="whiteAlpha.400">
                        {label}
                      </Text>
                      <Flex
                        align="center"
                        gap="4px"
                        px="8px"
                        py="2px"
                        borderRadius="full"
                        bg="rgba(255,255,255,0.06)"
                      >
                        <Box w="11px" h="11px" borderRadius="full" bg="rgba(255,255,255,0.08)" />
                        <Box w="28px" h="12px" borderRadius="2px" bg="rgba(255,255,255,0.08)" />
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {isCompleted && review?.dimensions && (
            <Box>
              <Flex align="center" gap="6px" mb="8px">
                <Box color="green.400" display="flex">
                  <LuShieldCheck size={13} />
                </Box>
                <Text textStyle="label" color="green.400" letterSpacing="0.08em">
                  Reviewed
                </Text>
              </Flex>
              <ReviewScoreTable dimensions={review.dimensions} />
            </Box>
          )}

          {isFailed && (
            <Box
              px="10px"
              py="9px"
              borderRadius="10px"
              bg="rgba(239, 68, 68, 0.06)"
              border="1px solid"
              borderColor="rgba(239, 68, 68, 0.14)"
            >
              <Text textStyle="caption" color="red.300" mb="6px">
                Review could not be completed. Please try again later.
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="red.300"
                _hover={{ bg: 'rgba(239, 68, 68, 0.1)' }}
                onClick={handleTriggerReview}
                loading={triggerMutation.isPending}
              >
                Retry
              </Button>
            </Box>
          )}
        </Flex>
      )}

      {activeTab === 'information' && (
        <Flex flexDirection="column" gap="12px">
          {!!colorSwatches.length && (
            <Box pt="2px">
              <Text textStyle="caption" color="whiteAlpha.500" mb="6px">
                Color palette
              </Text>
              <Flex gap="6px" flexWrap="wrap">
                {colorSwatches.map((color) => (
                  <Box
                    key={`${color.hex}-${color.name}`}
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    bg={color.hex}
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.25)"
                  />
                ))}
              </Flex>
            </Box>
          )}

          {aspectRatio && (
            <Box pt="2px">
              <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
                Aspect ratio
              </Text>
              <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
                {aspectRatio}
              </Text>
            </Box>
          )}

          {wildness && (
            <Box pt="2px">
              <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
                Creativity
              </Text>
              <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
                {wildness}
              </Text>
            </Box>
          )}

          {visualStyle && (
            <Box pt="2px">
              <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
                Visual style
              </Text>
              <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
                {visualStyle}
              </Text>
            </Box>
          )}

          {description && (
            <DescriptionToggle description={description} />
          )}

          {!!design.subjectImageUrls?.length && (
            <Box>
              <Text textStyle="caption" color="whiteAlpha.500" mb="6px">
                Subject Images
              </Text>
              <Flex gap="6px" flexWrap="wrap">
                {design.subjectImageUrls.map((url, i) => (
                  <Image
                    key={i}
                    src={url}
                    alt={`Subject ${i + 1}`}
                    w="32px"
                    h="32px"
                    objectFit="cover"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.16)"
                  />
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
      )}

      {activeTab === 'integrations' && (
        <ProPluginsPanel hideHeader />
      )}
      </Box>
    </Flex>
  )
}
